import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useTimeZone, useUserData } from '../../../../../hooks'
import { isStaffUser } from '../../../../../utils/user'
import { useNewsboardPostProfileContext } from '../../../Newsboard.hooks'
import {
  getNewsboardPostTimeRange,
  isNewsboardPostOwnerRestrictedFieldVisible,
} from '../../../Newsboard.utils'
import { NewsboardPostProfileHeaderInfoItem } from '../../NewsboardPostProfileHeaderInfoItem'
import { NewsboardPostProfileHeaderInfoItemComments } from '../../NewsboardPostProfileHeaderInfoItemComments'
import {
  NEWSBOARD_POST_PROFILE_HEADER_INFO_ITEMS_LOADING_ITEMS_LENGTH,
  NEWSBOARD_POST_PROFILE_HEADER_INFO_ITEMS_MAX_ITEMS_IN_ROW,
  NewsboardPostProfileHeaderInfoItemName,
} from '../../NewsboardPostProfileHeaderInfoItems.constants'
import {
  getEditActionHandlers,
  getNewsboardPostProfileHeaderInfoItems,
  isNewsboardPostProfileHeaderInfoItemNameComments,
} from '../../NewsboardPostProfileHeaderInfoItems.utils'
import { useNewsboardPostProfileEditPostAge } from '../useNewsboardPostProfileEditPostAge'
import { useNewsboardPostProfileEditTimeRange } from '../useNewsboardPostProfileEditTimeRange'

export function useNewsboardPostProfileHeaderInfoItems() {
  const { t } = useTranslation()

  const timeZone = useTimeZone()

  const user = useUserData()

  const { handleUpdateEntity, postData } = useNewsboardPostProfileContext()

  const [isTogglingComments, setTogglingComments] = useState(false)

  const { handleOpenModal: handleOpenEditTimeRangeModal } =
    useNewsboardPostProfileEditTimeRange()
  const { handleOpenModal: handleOpenEditPostAgeModal } =
    useNewsboardPostProfileEditPostAge()

  const timeRange = getNewsboardPostTimeRange(postData, timeZone)

  const isPostAgeVisibleToUser = isNewsboardPostOwnerRestrictedFieldVisible(
    postData,
    user
  )

  const items = getNewsboardPostProfileHeaderInfoItems({
    isCommentsToggleVisible: Boolean(user) && isStaffUser(user),
    isPostAgeVisibleToUser,
    postData,
    t,
    timeRange,
  })

  const handlers = {
    [NewsboardPostProfileHeaderInfoItemName.PostAge]: getEditActionHandlers(
      handleOpenEditPostAgeModal
    ),
    [NewsboardPostProfileHeaderInfoItemName.TimeRange]: getEditActionHandlers(
      handleOpenEditTimeRangeModal
    ),
  }

  async function handleToggleComments(checked) {
    setTogglingComments(true)

    try {
      await handleUpdateEntity({ comments_forbidden: !checked })
    } finally {
      setTogglingComments(false)
    }
  }

  function renderItem({ key, name, ...props }) {
    if (isNewsboardPostProfileHeaderInfoItemNameComments(name)) {
      return (
        <NewsboardPostProfileHeaderInfoItemComments
          {...props}
          disabled={isTogglingComments}
          key={key}
          name={name}
          onChange={handleToggleComments}
        />
      )
    }

    return (
      <NewsboardPostProfileHeaderInfoItem {...props} key={key} name={name} />
    )
  }

  const infoItemsProps = {
    handlers,
    items,
    loadingItemsLength:
      NEWSBOARD_POST_PROFILE_HEADER_INFO_ITEMS_LOADING_ITEMS_LENGTH,
    maxItemsInRow: NEWSBOARD_POST_PROFILE_HEADER_INFO_ITEMS_MAX_ITEMS_IN_ROW,
    maxLoadingItemsInRow:
      NEWSBOARD_POST_PROFILE_HEADER_INFO_ITEMS_MAX_ITEMS_IN_ROW,
    renderItem,
  }

  return { infoItemsProps }
}
